import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CheckCircle, Download, LogOut, Mail, Phone, Store, Trash2, Upload, Wallet } from 'lucide-react';
import type { User } from '../../App';
import { api } from '../../lib/api';

interface DeliveryPartnerApplicationPageProps {
  user: User;
  onLogout: () => void;
  onOpenDashboard: () => void;
}

type PartnerProfile = {
  vehicleType: 'Bike' | 'Cycle' | 'Scooter';
  vehicleNumber: string;
  deliveryArea: string;
  aadhaarFileName: string;
  aadhaarFileData: string;
  licenseFileName: string;
  licenseFileData: string;
  bankAccountName: string;
  bankName: string;
  branchName: string;
  bankAccountNumber: string;
  bankIfsc: string;
};

type DeliveryApplicationRecord = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  vehicleType: string;
  vehicleNumber?: string | null;
  licenseNumber?: string | null;
  drivingLicenseUrl?: string | null;
  drivingLicenseName?: string | null;
  deliveryArea?: string | null;
  idProofUrl?: string | null;
  idProofName?: string | null;
  bankAccountName?: string | null;
  bankName?: string | null;
  branchName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  reviewNotes?: string | null;
  reviewedAt?: string | null;
  activatedAt?: string | null;
};

const defaultProfile = (user: User): PartnerProfile => ({
  vehicleType: 'Bike',
  vehicleNumber: '',
  deliveryArea: '',
  aadhaarFileName: '',
  aadhaarFileData: '',
  licenseFileName: '',
  licenseFileData: '',
  bankAccountName: user.name || '',
  bankName: '',
  branchName: '',
  bankAccountNumber: '',
  bankIfsc: '',
});

export function DeliveryPartnerApplicationPage({
  user,
  onLogout,
  onOpenDashboard,
}: DeliveryPartnerApplicationPageProps) {
  const [application, setApplication] = useState<DeliveryApplicationRecord | null>(null);
  const [profile, setProfile] = useState<PartnerProfile>(() => defaultProfile(user));
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pendingAadhaarFile, setPendingAadhaarFile] = useState<File | null>(null);
  const [pendingLicenseFile, setPendingLicenseFile] = useState<File | null>(null);
  const aadhaarInputRef = useRef<HTMLInputElement | null>(null);
  const licenseInputRef = useRef<HTMLInputElement | null>(null);

  const isProfileIncomplete = useMemo(
    () =>
      !String(profile.vehicleNumber || '').trim() ||
      !String(profile.deliveryArea || '').trim() ||
      !String(profile.aadhaarFileData || '').trim() ||
      !String(profile.licenseFileData || '').trim() ||
      !String(profile.bankAccountName || '').trim() ||
      !String(profile.bankName || '').trim() ||
      !String(profile.branchName || '').trim() ||
      !String(profile.bankAccountNumber || '').trim() ||
      !String(profile.bankIfsc || '').trim(),
    [profile]
  );

  useEffect(() => {
    const loadApplication = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await api.deliveryApplications.getMine();
        const record = response.data || null;
        setApplication(record);

        if (record) {
          setProfile({
            ...defaultProfile(user),
            vehicleType: (record.vehicleType as PartnerProfile['vehicleType']) || 'Bike',
            vehicleNumber: record.vehicleNumber || '',
            deliveryArea: record.deliveryArea || '',
            aadhaarFileName: record.idProofName || '',
            aadhaarFileData: record.idProofUrl || '',
            licenseFileName: record.drivingLicenseName || '',
            licenseFileData: record.drivingLicenseUrl || '',
            bankAccountName: record.bankAccountName || user.name || '',
            bankName: record.bankName || '',
            branchName: record.branchName || '',
            bankAccountNumber: record.bankAccountNumber || '',
            bankIfsc: record.bankIfsc || '',
          });
        }
      } catch (loadError: any) {
        setApplication(null);
        setError(loadError?.message || '');
      } finally {
        setIsLoading(false);
      }
    };

    void loadApplication();
  }, [user]);

  const updateProfile = (next: Partial<PartnerProfile>) => {
    setProfile((prev) => ({ ...prev, ...next }));
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Unable to read file'));
      reader.readAsDataURL(file);
    });

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'aadhaar' | 'license'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    if (type === 'aadhaar') {
      setPendingAadhaarFile(file);
      return;
    }
    setPendingLicenseFile(file);
  };

  const handleUploadSelectedFile = async (type: 'aadhaar' | 'license') => {
    const file = type === 'aadhaar' ? pendingAadhaarFile : pendingLicenseFile;
    if (!file) return;

    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (type === 'aadhaar') {
        updateProfile({ aadhaarFileName: file.name, aadhaarFileData: dataUrl });
        setPendingAadhaarFile(null);
        if (aadhaarInputRef.current) aadhaarInputRef.current.value = '';
      } else {
        updateProfile({ licenseFileName: file.name, licenseFileData: dataUrl });
        setPendingLicenseFile(null);
        if (licenseInputRef.current) licenseInputRef.current.value = '';
      }
    } catch (fileError: any) {
      setError(fileError?.message || 'File upload failed');
    }
  };

  const handleFileDownload = async (fileName: string, fileData: string, fallbackName: string) => {
    if (!fileData) return;

    try {
      let href = fileData;
      let revokeUrl = false;

      if (!fileData.startsWith('data:')) {
        const response = await fetch(fileData);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        href = window.URL.createObjectURL(blob);
        revokeUrl = true;
      }

      const link = document.createElement('a');
      link.href = href;
      link.download = fileName || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (revokeUrl) {
        window.URL.revokeObjectURL(href);
      }
    } catch {
      setError('Unable to download file');
    }
  };

  const handleFileDelete = (type: 'aadhaar' | 'license') => {
    if (type === 'aadhaar') {
      updateProfile({ aadhaarFileName: '', aadhaarFileData: '' });
      setPendingAadhaarFile(null);
      if (aadhaarInputRef.current) aadhaarInputRef.current.value = '';
      return;
    }

    updateProfile({ licenseFileName: '', licenseFileData: '' });
    setPendingLicenseFile(null);
    if (licenseInputRef.current) licenseInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (isProfileIncomplete) {
      setError('Please fill all required application fields.');
      return;
    }

    setIsSubmitting(true);
    const wasApproved = application?.status === 'APPROVED';
    const wasSubmitted = Boolean(application);
    try {
      const response = await api.deliveryApplications.submitMine({
        phone: user.phone || '',
        vehicleType: profile.vehicleType,
        vehicleNumber: profile.vehicleNumber,
        drivingLicenseFile: profile.licenseFileData,
        drivingLicenseName: profile.licenseFileName,
        deliveryArea: profile.deliveryArea,
        idProofFile: profile.aadhaarFileData,
        idProofName: profile.aadhaarFileName,
        bankAccountName: profile.bankAccountName,
        bankName: profile.bankName,
        branchName: profile.branchName,
        bankAccountNumber: profile.bankAccountNumber,
        bankIfsc: profile.bankIfsc,
      });
      setApplication(response.data || null);
      if (wasApproved) {
        setMessage('Application updated successfully. Changes may require re-approval.');
      } else if (wasSubmitted) {
        setMessage('Application updated successfully.');
      } else {
        setMessage('Application submitted successfully. Wait for super admin approval.');
      }
    } catch (submitError: any) {
      setError(submitError?.message || 'Unable to submit application.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      <div className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <Store className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-gray-900">FoodHub</h1>
                <p className="text-gray-600 text-sm">Delivery Partner Application</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {application?.status === 'APPROVED' && (
                <button
                  onClick={onOpenDashboard}
                  className="px-4 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 text-sm font-medium"
                >
                  Open Dashboard
                </button>
              )}
              <button
                onClick={onLogout}
                className="px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h2 className="text-gray-900 mb-3 text-4xl">Complete Delivery Partner Application</h2>
          {/* <p className="text-gray-600 text-lg">
            Sign in ke baad yeh form alag page pe open hoga. Approval ke baad hi dashboard fully active hoga.
          </p> */}
        </div>

        <div className="max-w-4xl mx-auto bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Applicant</p>
              <p className="font-semibold text-gray-900">{user.name}</p>
              <p className="text-sm text-gray-600 flex items-center gap-2 mt-1"><Mail className="w-4 h-4" /> {user.email}</p>
              <p className="text-sm text-gray-600 flex items-center gap-2 mt-1"><Phone className="w-4 h-4" /> {user.phone || '-'}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Application Status</p>
              <p className="font-semibold text-gray-900">{application?.status || 'NOT_SUBMITTED'}</p>
              {application?.reviewNotes && <p className="text-sm text-gray-600 mt-1">{application.reviewNotes}</p>}
              {application?.status === 'APPROVED' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3">
                  You can edit and resubmit this application. Depending on changes, it may go back to review.
                </p>
              )}
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Activation</p>
              <p className="font-semibold text-gray-900">
                {application?.activatedAt ? new Date(application.activatedAt).toLocaleString() : 'Pending'}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="py-10 text-center text-gray-500">Loading application...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-orange-50 border border-orange-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Vehicle & KYC</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Vehicle Type</label>
                      <select
                        value={profile.vehicleType}
                        onChange={(e) => updateProfile({ vehicleType: e.target.value as PartnerProfile['vehicleType'] })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                      >
                        <option>Bike</option>
                        <option>Cycle</option>
                        <option>Scooter</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Vehicle Number</label>
                      <input
                        value={profile.vehicleNumber}
                        onChange={(e) => updateProfile({ vehicleNumber: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Enter vehicle number"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Delivery Area</label>
                      <input
                        value={profile.deliveryArea}
                        onChange={(e) => updateProfile({ deliveryArea: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Enter delivery area"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Aadhaar / ID Upload</label>
                      {!profile.aadhaarFileData ? (
                        <div className="flex items-center gap-3">
                          <input
                            ref={aadhaarInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(e) => void handleFileChange(e, 'aadhaar')}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => void handleUploadSelectedFile('aadhaar')}
                            disabled={!pendingAadhaarFile}
                            className="inline-flex items-center justify-center w-10 h-10 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                            title="Upload Aadhaar / ID"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            readOnly
                            value={profile.aadhaarFileName || 'aadhaar-id'}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white text-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              void handleFileDownload(profile.aadhaarFileName, profile.aadhaarFileData, 'aadhaar-id')
                            }
                            className="inline-flex items-center justify-center w-10 h-10 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                            title="Download Aadhaar / ID"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFileDelete('aadhaar')}
                            className="inline-flex items-center justify-center w-10 h-10 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                            title="Delete Aadhaar / ID"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Driving License Upload</label>
                      {!profile.licenseFileData ? (
                        <div className="flex items-center gap-3">
                          <input
                            ref={licenseInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(e) => void handleFileChange(e, 'license')}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => void handleUploadSelectedFile('license')}
                            disabled={!pendingLicenseFile}
                            className="inline-flex items-center justify-center w-10 h-10 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                            title="Upload driving license"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            readOnly
                            value={profile.licenseFileName || 'driving-license'}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white text-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              void handleFileDownload(profile.licenseFileName, profile.licenseFileData, 'driving-license')
                            }
                            className="inline-flex items-center justify-center w-10 h-10 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                            title="Download driving license"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFileDelete('license')}
                            className="inline-flex items-center justify-center w-10 h-10 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                            title="Delete driving license"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 border border-green-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-green-600" />
                    Bank Account Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Account Holder Name</label>
                      <input
                        value={profile.bankAccountName}
                        onChange={(e) => updateProfile({ bankAccountName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Account holder name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Bank Name</label>
                      <input
                        value={profile.bankName}
                        onChange={(e) => updateProfile({ bankName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Bank name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Branch Name</label>
                      <input
                        value={profile.branchName}
                        onChange={(e) => updateProfile({ branchName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Branch name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">Account Number</label>
                      <input
                        value={profile.bankAccountNumber}
                        onChange={(e) => updateProfile({ bankAccountNumber: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="Account number"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 text-sm font-medium">IFSC Code</label>
                      <input
                        value={profile.bankIfsc}
                        onChange={(e) => updateProfile({ bankIfsc: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        placeholder="IFSC code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {error && <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">{error}</div>}
              {message && <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-xl">{message}</div>}

              <div className="flex justify-end gap-3">
                {application?.status === 'APPROVED' && (
                  <button
                    type="button"
                    onClick={onOpenDashboard}
                    className="px-5 py-3 rounded-xl bg-green-100 text-green-700 font-medium hover:bg-green-200 flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Open Dashboard
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-medium hover:from-orange-600 hover:to-red-700 disabled:opacity-60 flex items-center gap-2"
                >
                  {isSubmitting ? 'Submitting...' : application ? 'Update Application' : 'Submit Application'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
